package er.prateek.crossly.ui.components

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.spring
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import androidx.compose.material3.MaterialTheme
import kotlinx.coroutines.delay

/**
 * The game's core reward readout.
 *
 * With [animated] set, earned stars stamp in one at a time — overshoot scale
 * plus a spin — which is the moment the results screen is built around. Static
 * mode is used everywhere stars are just information (level tiles, headers).
 */
@Composable
fun StarRating(
    stars: Int,
    modifier: Modifier = Modifier,
    maxStars: Int = 3,
    starSize: Dp = 20.dp,
    spacing: Dp = Spacing.xxs,
    animated: Boolean = false,
) {
    val colors = MaterialTheme.game
    val filled = stars.coerceIn(0, maxStars)

    Row(
        modifier = modifier,
        horizontalArrangement = Arrangement.spacedBy(spacing),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        repeat(maxStars) { index ->
            val earned = index < filled
            val scale = remember(stars, index) { Animatable(if (animated && earned) 0f else 1f) }
            val spin = remember(stars, index) { Animatable(if (animated && earned) -0.5f else 0f) }

            if (animated && earned) {
                LaunchedEffect(stars, index) {
                    delay(index * 220L)
                    scale.animateTo(
                        targetValue = 1f,
                        animationSpec = spring(
                            dampingRatio = Spring.DampingRatioMediumBouncy,
                            stiffness = Spring.StiffnessLow,
                        ),
                    )
                }
                LaunchedEffect(stars, index) {
                    delay(index * 220L)
                    spin.animateTo(0f, spring(stiffness = Spring.StiffnessVeryLow))
                }
            }

            Canvas(
                modifier = Modifier
                    .size(starSize)
                    .graphicsLayer {
                        scaleX = scale.value
                        scaleY = scale.value
                        rotationZ = spin.value * 360f
                    },
            ) {
                val center = Offset(size.width / 2f, size.height / 2f)
                val radius = size.minDimension / 2f
                if (earned) {
                    drawStar(
                        center = center,
                        radius = radius,
                        color = colors.gold,
                        brush = Brush.linearGradient(
                            colors = listOf(colors.gold, colors.flame),
                            start = Offset(center.x - radius, center.y - radius),
                            end = Offset(center.x + radius, center.y + radius),
                        ),
                    )
                } else {
                    drawStar(center = center, radius = radius, color = colors.goldEmpty)
                }
            }
        }
    }
}
